import { useState } from "react";
import { DayPicker } from "react-day-picker";

import { cx } from "@/lib/cx";
import {
  formatDateKey,
  fromDateKey,
  isDateKey,
  relativeDayLabel,
  toDateKey,
  todayKey,
  tryFromDateKey,
  type DateKey,
} from "@/lib/dates";
import { control, type ControlContext, type ControlSize } from "@/ui/control";
import { Button } from "@/ui/Button";
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon, CloseCircleIcon } from "@/ui/icons";
import { Popover } from "./Popover";

/**
 * A date field that is part of the design.
 *
 * The two `<input type="date">` fields this replaces (UI-REVIEW P14) rendered
 * `dd/mm/yyyy` in the browser's own chrome, in the middle of an otherwise
 * designed interface, and on a desktop browser they are close to unusable — a
 * three-segment spinner with a tiny picker glyph.
 *
 * It speaks the app's own date type. Everything in Kobo is bucketed by a
 * `YYYY-MM-DD` local calendar key, and `lib/dates.ts` exists because deriving
 * that key from a `Date` the wrong way silently breaks a streak the user has
 * kept for months. So the value in and out is a `DateKey`; the `Date` objects
 * live only inside this component, where `fromDateKey` builds them at local noon
 * and `toDateKey` reads them back.
 *
 * Keyboard: the calendar is `react-day-picker`, which owns arrow keys, PageUp
 * and PageDown for months, and Home/End. Opening focuses the selected day, or
 * today when nothing is selected.
 */

export type DatePickerProps = {
  /** `YYYY-MM-DD`, or null for empty. */
  value: DateKey | null;
  onChange: (value: DateKey | null) => void;
  /** Shown on the trigger when there is no value. */
  placeholder?: string;
  /** Adds a ⊗ on the trigger. Off when the field is required. */
  clearable?: boolean;
  /** Earliest and latest selectable day, inclusive. */
  min?: DateKey;
  max?: DateKey;
  /**
   * What counts as today, for the marker and for the "Today" shortcut.
   * Injectable so a caller that already knows the day (every day-bucketed
   * screen does) cannot disagree with itself, and so tests can pin it.
   */
  today?: DateKey;
  /** Renders `Today` / `Tomorrow` on the trigger where that is true. */
  relative?: boolean;
  size?: ControlSize;
  on?: ControlContext;
  disabled?: boolean;
  invalid?: boolean;
  fullWidth?: boolean;
  "aria-label"?: string;
  id?: string;
  align?: "start" | "center" | "end";
  className?: string;
};

export function DatePicker({
  value,
  onChange,
  placeholder = "Pick a date",
  clearable = true,
  min,
  max,
  today = todayKey(),
  relative = false,
  size = "md",
  on = "panel",
  disabled = false,
  invalid = false,
  fullWidth = true,
  "aria-label": ariaLabel,
  id,
  align = "start",
  className,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);

  const selected = value && isDateKey(value) ? fromDateKey(value) : undefined;
  const label = value && isDateKey(value)
    ? relative
      ? relativeDayLabel(value, today, "medium")
      : formatDateKey(value, "medium")
    : placeholder;
  const showClear = clearable && Boolean(value);

  return (
    // The clear affordance is a *sibling* of the trigger, layered over its right
    // edge, not a child of it. It used to be a `<span role="button">` inside the
    // trigger `<button>` — which avoids the invalid nested-button markup but
    // costs more than it saves: ARIA makes every descendant of a button
    // presentational, so the clear control was stripped out of the accessibility
    // tree entirely, and its text still leaked into the trigger's computed name
    // ("31 Aug 2026 Clear date"). Two siblings give two real controls with two
    // real names, and the trigger keeps `aria-expanded` to itself.
    <span className={cx("relative inline-flex", fullWidth ? "w-full" : "w-auto")}>
      <Popover
        open={open}
        onOpenChange={setOpen}
        align={align}
        padded={false}
        label="Choose a date"
        trigger={
          <button
            type="button"
            id={id}
            disabled={disabled}
            aria-label={ariaLabel}
            className={control({
              size,
              on,
              invalid,
              fullWidth,
              className: cx(
                "inline-flex items-center gap-2 text-left",
                !value && "text-muted",
                // Room for the overlaid ⊗, so a long date never runs under it.
                showClear && "pr-9",
                "pointer-coarse:min-h-tap",
                className,
              ),
            })}
          >
            <CalendarIcon className="size-4 shrink-0 text-faint" />
            <span className="min-w-0 flex-1 truncate">{label}</span>
          </button>
        }
      >
        <Calendar
          selected={selected}
          today={today}
          min={min}
          max={max}
          onSelect={(next) => {
            onChange(next);
            setOpen(false);
          }}
        />
      </Popover>

      {showClear ? (
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          disabled={disabled}
          aria-label="Clear date"
          onClick={() => onChange(null)}
          className="absolute inset-y-0 end-3 my-auto text-faint"
        >
          {/* The filled ⊗, which is what iOS puts inside a field to clear it. A
              stroked ✕ at 12px reads as decoration next to 15px body text. */}
          <CloseCircleIcon className="size-5" />
        </Button>
      ) : null}
    </span>
  );
}

/* -------------------------------------------------------------------------- */

export type CalendarProps = {
  selected?: Date;
  today: DateKey;
  min?: DateKey;
  max?: DateKey;
  onSelect: (value: DateKey | null) => void;
};

/**
 * The calendar body, exported on its own so a surface that already has a popover
 * — the day navigator, which wants the calendar attached to its own date button
 * — can drop it in without nesting two poppers.
 *
 * **This export's shape is load-bearing**: `ui/DayNavigator.tsx` renders
 * `<Calendar>` directly. The props are unchanged by the iOS pass.
 *
 * Every `react-day-picker` class name is replaced rather than extended, which is
 * why the library's stylesheet is deliberately not imported: nothing of its
 * default look survives, so shipping it would only be bytes and specificity
 * fights.
 */
export function Calendar({ selected, today, min, max, onSelect }: CalendarProps) {
  const todayDate = fromDateKey(today);

  return (
    <div className="p-2">
      <DayPicker
        mode="single"
        selected={selected}
        onSelect={(next) => onSelect(next ? toDateKey(next) : null)}
        defaultMonth={selected ?? todayDate}
        today={todayDate}
        startMonth={min ? (tryFromDateKey(min) ?? undefined) : undefined}
        endMonth={max ? (tryFromDateKey(max) ?? undefined) : undefined}
        disabled={[
          ...(min ? [{ before: fromDateKey(min) }] : []),
          ...(max ? [{ after: fromDateKey(max) }] : []),
        ]}
        showOutsideDays
        autoFocus
        components={{
          PreviousMonthButton: (props) => (
            <Button {...props} variant="plain" size="sm" iconOnly>
              <ChevronLeftIcon className="size-5" />
            </Button>
          ),
          NextMonthButton: (props) => (
            <Button {...props} variant="plain" size="sm" iconOnly>
              <ChevronRightIcon className="size-5" />
            </Button>
          ),
        }}
        classNames={CLASS_NAMES}
      />

      <div className="mt-1 flex justify-start hairline-t pt-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onSelect(today)}
          className="text-accent-ink"
        >
          Today
        </Button>
      </div>
    </div>
  );
}

/**
 * The month arrows. Tinted rather than neutral, because on iOS the month
 * chevrons are the calendar's one accent and the only two controls in the header.
 *
 * They grow to the 44px floor on a coarse pointer, and the caption row grows
 * with them (see `month_caption` / `nav` below) — these are the only way to
 * change month on a phone, and the picker is reachable there through
 * `ui/DayNavigator`. A `tapHalo` was the other option and is wrong here: the
 * two buttons sit 4px apart, so two 44px halos would overlap and a tap in the
 * gap would step the wrong way.
 */
/**
 * The calendar's whole appearance, keyed by `react-day-picker`'s UI parts.
 *
 * ## The three decisions worth stating
 *
 * **Days are circles.** Everything else in the app follows the radius ladder
 * (chip → control → group → panel → sheet), and a calendar day is the one
 * documented exception: iOS draws the day cell as a full circle, and a 10px
 * rounded square in a 7-column grid is the single clearest "this is a web date
 * picker" tell there is.
 *
 * **Today is a tint, selected is a fill.** If today were also a fill the two
 * would compete on the same channel, which is the P6 mistake in miniature. The
 * tint is scoped with `:not([data-selected])` rather than left to Tailwind's
 * emission order — `react-day-picker` puts both class names on the same `<td>`,
 * so `.today [&_button]:text-accent-ink` and `.selected [&_button]:text-selected-fg`
 * would be two rules at identical specificity and the winner would be whichever
 * the framework happened to write last. `.today:not([data-selected])>button`
 * outranks `.selected>button` by one attribute selector, deterministically, and
 * `data-selected` is on the cell because `react-day-picker` puts it there.
 *
 * **The desktop grid is 32px cells with no gaps** and grows to 44px per
 * interactive day on a coarse pointer.
 */
const CLASS_NAMES = {
  // `relative` because the nav buttons are absolutely positioned into the
  // caption row rather than sitting beside the month label.
  root: "relative text-label text-ink",
  months: "flex flex-col",
  month: "flex flex-col gap-1",
  // The caption row grows to 44px on a coarse pointer with its nav buttons.
  month_caption: "flex h-8 items-center px-2 pointer-coarse:min-h-tap",
  caption_label: "text-label text-ink",
  nav: "absolute right-1 top-1 flex items-center gap-1 pointer-coarse:right-0 pointer-coarse:top-0",
  month_grid: "w-full border-collapse",
  weekdays: "flex",
  // `text-subtle` rather than `text-faint`: Mo/Tu/We are text, at 12px, and
  // faint is 3.55:1 in light. Faint stays on the glyphs and disabled days.
  weekday: "size-8 text-caption tracking-wide text-subtle uppercase pointer-coarse:size-tap",
  week: "flex",
  day: "size-8 p-0 pointer-coarse:size-tap [&:not([data-selected])>button:hover]:bg-fill-3",
  day_button: cx(
    "flex size-8 items-center justify-center rounded-full text-label text-ink",
    "pointer-coarse:size-tap",
    "numeric dur-fast transition-colors",
  ),
  // No transition on the fill: selection is instantaneous (IOS-DESIGN §6).
  selected:
    "[&>button]:bg-selected [&>button]:text-selected-fg [&>button]:font-semibold [&>button]:transition-none",
  today: "[&:not([data-selected])>button]:text-accent-ink [&:not([data-selected])>button]:font-semibold",
  // `showOutsideDays` leaves the neighbouring month's days selectable, so those
  // numbers are live text and take `text-muted`, which remains legal over the
  // hover fill. A disabled day is exempt from AA and keeps `text-faint`.
  outside: "[&>button]:text-muted",
  disabled: "[&>button]:pointer-events-none [&>button]:text-faint",
  hidden: "invisible",
} as const;
